import { AxiosError, AxiosInstance } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { errorMessageAdapter, userRoleAdapter } from '../../adapters';
import { AccountService } from '../../services/AccountService';
import { useApi } from '../../services/hooks/apiHook';
import { PaymentStatus } from '../../services/Interfaces/IAccountService';
import { isPaymentMade } from '../../services/utils/mappingStatus';
import { useAuth } from '../../store/auth';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { errorToast } from '../../utils/toasts';
import { MID_TRANSFER_PROGRESS_STEPS } from '../../pages/Onboarding/CompanyTransfer/constants';

function CheckoutGuard({ element }: { element?: React.ReactNode | React.ComponentType }) {
  const [isCheckoutPaid, setIsCheckoutPaid] = useState<boolean | null>(null);
  const [checkoutStatus, setCheckoutStatus] = useState<PaymentStatus>();
  const [typeCompany, setCompany] = useState<string>();

  const { token, planSlug, user } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [, setTypeCompany] = useLocalStorage<string | null>('typeCompany', null);

  const api: AxiosInstance = useApi();

  const userRole = userRoleAdapter(user!.role_id) || 'customer';

  const lastCheckoutKeyRef = useRef<string | null>(null);

  const fetchCheckoutStatus = useCallback(async () => {
    if (userRole !== 'customer' || !token) {
      setIsCheckoutPaid(true);
      return;
    }

    const key = `${token ?? ''}|${planSlug ?? ''}`;
    if (lastCheckoutKeyRef.current === key) {
      return;
    }
    lastCheckoutKeyRef.current = key;

    try {
      const response = await AccountService.getCheckoutStatus(
        api,
        { slug_plan: planSlug },
        {
          headers: { 'Content-Language': i18n.language, Authorization: `Bearer ${token}` }
        }
      );

      if (!response) {
        const progressStep = user?.account?.progress_step;
        if (
          progressStep &&
          (MID_TRANSFER_PROGRESS_STEPS as readonly string[]).includes(progressStep)
        ) {
          navigate('/company-transfer');
        } else if (progressStep === 'concluded') {
          // Paid / finished account without checkout payload — stay on dashboard.
          setIsCheckoutPaid(true);
        } else {
          navigate('/choose-onboarding');
        }
        return;
      }

      setIsCheckoutPaid(isPaymentMade(response.data?.status));
      setCheckoutStatus(response.data.status);
      setCompany(response.data?.company_type);
    } catch (e) {
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  }, [api, i18n.language, navigate, planSlug, token, userRole, user?.account?.progress_step]);
  useEffect(() => {
    void fetchCheckoutStatus();
  }, [fetchCheckoutStatus]);

  useEffect(() => {
    if (!token) {
      lastCheckoutKeyRef.current = null;
    }
  }, [token]);

  useEffect(() => {
    if (!checkoutStatus) return;

    if ([PaymentStatus.Pending].includes(checkoutStatus)) {
      navigate('/payment-awaiting-approval');
      return;
    }

    // Unpaid / incomplete checkout: resume the matching flow — never dump paid or
    // mid-transfer users on choose-onboarding when company_type is known.
    if (checkoutStatus !== PaymentStatus.Confirmed && typeCompany) {
      setTypeCompany(typeCompany);
      navigate(typeCompany === 'new_company' ? '/onboarding' : '/company-transfer');
      return;
    }

    if ([PaymentStatus.Created].includes(checkoutStatus)) {
      navigate('/choose-onboarding');
    }
  }, [isCheckoutPaid, checkoutStatus, typeCompany, navigate, setTypeCompany]);

  if (isCheckoutPaid === null) {
    return <></>;
  }

  const Element: React.ComponentType = element as React.ComponentType;

  return <>{element && typeof element === 'function' ? <Element /> : element}</>;
}

export { CheckoutGuard };
